import { useState, type ChangeEvent } from "react";
import { syncApiUrl, getAuthToken, type PaymentMethodConfig } from "@cliqsuite/db";
import type { CfgTab, ConfigFormValue } from "../state/AdminDataContext";
import { Field, Modal, SwitchRow } from "./Modal";

interface FieldDef {
  label: string;
  key: keyof ConfigFormValue;
  placeholder: string;
  area?: boolean;
}

const FIELD_DEFS: Record<CfgTab, FieldDef[]> = {
  qr: [
    { label: "Display name", key: "name", placeholder: "e.g. KBZPay" },
    { label: "Payment instructions", key: "instructions", placeholder: "How to pay with this QR…", area: true },
  ],
  bank: [
    { label: "Bank name", key: "bank_name", placeholder: "e.g. KBZ Bank" },
    { label: "Account name", key: "account_name", placeholder: "Account holder" },
    { label: "Account number", key: "account_number", placeholder: "0000 0000 0000" },
    { label: "Branch", key: "branch", placeholder: "Branch (optional)" },
    { label: "Notes / instructions", key: "instructions", placeholder: "Reference instructions…", area: true },
  ],
  wallet: [
    { label: "Wallet label", key: "name", placeholder: "e.g. USDT (BEP20)" },
    { label: "Network", key: "network", placeholder: "BNB Smart Chain (BEP20)" },
    { label: "Wallet address", key: "wallet_address", placeholder: "0x…" },
    { label: "Payment instructions", key: "instructions", placeholder: "Network warnings, etc…", area: true },
  ],
};

const META: Record<CfgTab, { hasImage: boolean; imageLabel: string; titleNoun: string; sub: string }> = {
  qr: { hasImage: true, imageLabel: "QR code image", titleNoun: "QR code", sub: "Businesses scan it to pay CliqSuite" },
  bank: { hasImage: false, imageLabel: "", titleNoun: "bank account", sub: "Businesses transfer to it to pay CliqSuite" },
  wallet: { hasImage: true, imageLabel: "Wallet QR (optional)", titleNoun: "crypto wallet", sub: "Businesses send to it to pay CliqSuite" },
};

async function uploadImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(syncApiUrl("/uploads/platform-payment-method-image"), {
    method: "POST",
    headers: getAuthToken() ? { authorization: `Bearer ${getAuthToken()}` } : {},
    body: formData,
  });
  if (!res.ok) throw new Error("upload failed");
  const { url } = (await res.json()) as { url: string };
  return url;
}

export function ConfigModal({
  tab,
  existing,
  onClose,
  onSubmit,
  onDelete,
}: {
  tab: CfgTab;
  existing: PaymentMethodConfig | null;
  onClose: () => void;
  onSubmit: (item: ConfigFormValue) => void;
  onDelete?: () => void;
}) {
  const [form, setForm] = useState<Record<string, unknown>>(() => (existing ? { ...existing } : { is_enabled: true }));
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const meta = META[tab];
  const fields = FIELD_DEFS[tab];
  const enabled = form.is_enabled !== false;
  const image = (form.image_data as string | null) ?? null;

  const setField = (patch: Record<string, unknown>) => setForm((prev) => ({ ...prev, ...patch }));

  const handleImage = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadErr("");
    try {
      const url = await uploadImage(file);
      setField({ image_data: url });
    } catch {
      setUploadErr("Couldn't upload the image. Try again.");
    } finally {
      setUploading(false);
    }
  };

  const requiredKey: keyof ConfigFormValue = tab === "qr" ? "name" : tab === "bank" ? "bank_name" : "wallet_address";
  const requiredValue = String(form[requiredKey] ?? "").trim();

  const submit = () => {
    if (!requiredValue) return;
    const displayName = tab === "bank" ? requiredValue : String(form.name ?? "").trim() || requiredValue;
    const item: ConfigFormValue = {
      name: displayName,
      instructions: String(form.instructions ?? "").trim() || null,
      bank_name: tab === "bank" ? requiredValue : null,
      account_name: tab === "bank" ? String(form.account_name ?? "").trim() || null : null,
      account_number: tab === "bank" ? String(form.account_number ?? "").trim() || null : null,
      branch: tab === "bank" ? String(form.branch ?? "").trim() || null : null,
      network: tab === "wallet" ? String(form.network ?? "BNB Smart Chain (BEP20)").trim() || null : null,
      wallet_address: tab === "wallet" ? requiredValue : null,
      image_data: image,
      is_enabled: enabled,
    };
    onSubmit(item);
  };

  return (
    <Modal
      onClose={onClose}
      width={500}
      title={`${existing ? "Edit" : "Add"} ${meta.titleNoun}`}
      sub={meta.sub}
      footer={
        <>
          {onDelete && (
            <button type="button" className="o-btn o-btn--danger" onClick={onDelete}>
              <span className="ms" aria-hidden="true">delete</span>
              Delete
            </button>
          )}
          <span style={{ flex: 1 }} />
          <button type="button" onClick={onClose} className="o-btn o-btn--grey">
            Cancel
          </button>
          <button type="button" onClick={submit} className="o-btn o-btn--ink" disabled={!requiredValue || uploading}>
            <span className="ms" aria-hidden="true">check</span>
            {existing ? "Save changes" : "Add method"}
          </button>
        </>
      }
    >
      {fields.map((f) => (
        <Field key={f.key} label={f.label}>
          {f.area ? (
            <textarea className="o-input o-input--area" aria-label={f.label} value={String(form[f.key] ?? "")} onChange={(e) => setField({ [f.key]: e.target.value })} rows={2} placeholder={f.placeholder} />
          ) : (
            <input className="o-input" aria-label={f.label} value={String(form[f.key] ?? "")} onChange={(e) => setField({ [f.key]: e.target.value })} placeholder={f.placeholder} />
          )}
        </Field>
      ))}

      {meta.hasImage && (
        <Field label={meta.imageLabel}>
          <div className="o-photo">
            <span className="o-photo__img">{image ? <img src={image} alt="" /> : <span className="ms" aria-hidden="true">qr_code_2</span>}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 800 }}>{image ? "Image added" : "No image yet"}</div>
              <div className="o-row__sub" style={{ whiteSpace: "normal" }}>{uploadErr || "PNG or JPG. Businesses scan it when they pay."}</div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <label className="o-btn o-btn--line" style={{ height: 36, padding: "0 12px", fontSize: 13, cursor: uploading ? "default" : "pointer", opacity: uploading ? 0.5 : 1 }}>
                  <span className="ms" aria-hidden="true">upload</span>
                  {uploading ? "Uploading…" : image ? "Replace" : "Upload QR"}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handleImage} disabled={uploading} style={{ display: "none" }} />
                </label>
                {image && (
                  <button type="button" className="o-btn o-btn--line" style={{ height: 36, padding: "0 12px", fontSize: 13 }} onClick={() => setField({ image_data: null })}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        </Field>
      )}

      <SwitchRow label="Enabled for customers" checked={enabled} onChange={(v) => setField({ is_enabled: v })} />
    </Modal>
  );
}
